import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { useRemote } from './useRemote';
import { ReservationForTill } from '../wire';
import type { Product, RemainderKind, Service } from '../wire';
import { formatMoney, isoDayIn } from '../mirror/time';
import { centsFromInput } from '../money/input';
import { certificateState, findCertificateByCode } from '../money/certificates';
import { readRecents, rememberRecents } from '../money/recents';
import { previewWaterfall } from '../money/waterfall';
import type { PreviewInput, PreviewLine, WaterfallPreview } from '../money/waterfall';
import type { MirroredAppointment } from '../db/schema';
import { useLocale, useT } from '../messages';
import { Catalog } from './checkout/Catalog';
import { CartLines, Totals } from './checkout/Cart';
import { GuestField } from './checkout/GuestField';
import { Payment } from './checkout/Payment';
import { PriceSheet } from './checkout/PriceSheet';
import {
  linesFromAppointment,
  nextKey,
  nextTenderKey,
  tillTendersFor,
  toActionPart,
} from './checkout/model';
import type { CompletedSale, LineDraft, ServiceOption, TenderDraft } from './checkout/model';

export { tillTendersFor };

/**
 * **Checkout** — a settlement, not an order (spec §6.5), drawn as a cart.
 *
 * The same document for a visit being closed out and for a quick sale, because
 * they are the same document: lines over `items`, then the tender waterfall,
 * then one commit. A separate "quick sale" screen would drift from this one.
 *
 * ## The screen (2026-09-30)
 *
 * The owner's verdict on the previous screen was that the concept is simple —
 * *it's a cart, a sale* — and the screen was not. So it is now two areas, the
 * way every counter POS draws it:
 *
 *  - **the catalog** (`checkout/Catalog.tsx`) — search, Services / Products,
 *    her menu headings, tiles with name, price and length; a service sold
 *    several ways asks which; attach-only add-ons appear as "Add to <parent>"
 *    once the parent is in the cart;
 *  - **the cart** (`checkout/Cart.tsx`, `checkout/Payment.tsx`) — who it is for
 *    (Walk-in by default), the lines with a − qty + stepper, subtotal, discount
 *    and tax behind quiet links, the total, "How was it paid?" as big buttons,
 *    and one button: *Charge $78.00 · Cash*.
 *
 * Side by side on an iPad, stacked on a phone with the total and the Charge
 * button in a bar that never leaves the bottom of the screen. The number pad
 * appears only while a price is being typed (`checkout/PriceSheet.tsx`).
 *
 * ## What did not change
 *
 * **The money.** The waterfall's order (manual discount, certificate, deposit,
 * credit gift-then-cash, the rest) is the server's and every step caps at what
 * remains; `money/waterfall.ts` transcribes it so the total can move while she
 * taps, and it is never sent. The request names instruments — a code, "use the
 * deposit", a tender — and `POST /v1/settlements` decides every amount (B7).
 * The intent, its payload and the outbox are exactly what they were: a checkout
 * in a basement is a checkout, and the appointment shows completed the moment
 * it lands. The waterfall's numbering and its internal explanations are simply
 * no longer printed.
 *
 * **One thing the till no longer offers**: typing over a priced item's list
 * price. A discount is how she gives something away, and it is one link under
 * the subtotal. Only an open-price line's typed amount and a pickup order's
 * quoted price ever ride as `unit_price_cents`, and the server re-prices every
 * line regardless.
 */

export interface CheckoutPrefill {
  /** The visit being closed out. Absent is a quick sale. */
  appointment?: MirroredAppointment | undefined;
  /**
   * **A pickup order being collected** (C1 → B4), staged by the Reservations
   * screen and taken by whoever mounts this sheet (`ui/till.ts`). A *draft*:
   * the lines come back at the price the guest was quoted, and a cashier may
   * edit, add or remove anything before ringing it up.
   */
  reservationId?: string | undefined;
}

/** Which open-price line the price sheet is for: one being added, or one being edited. */
type Pricing = { kind: 'new'; product: Product } | { kind: 'edit'; key: string };

export function CheckoutSheet({
  prefill,
  onDone,
  onCancel,
  afterCheckout,
}: {
  prefill: CheckoutPrefill;
  onDone: () => void;
  onCancel: () => void;
  /**
   * ## MOUNT POINT — the desk agent's `<AddClientNote>` (B1)
   *
   * A render prop, called with the guest the sale settled against and rendered
   * in the completed panel. `clientId` is `null` for a walk-in — the built-in
   * account is a real row, but a note about nobody is a note the owner will
   * never find again. Absent is the ordinary state.
   */
  afterCheckout?:
    ((sale: { clientId: string | null; clientName: string | null }) => ReactNode) | undefined;
}): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const locale = useLocale();

  const session = useLive(() => runtime.mirror.session(), null);
  const currency = session?.tenant.currency ?? 'USD';
  const timezone = session?.tenant.timezone ?? 'UTC';
  const today = isoDayIn(timezone, runtime.now());
  const modules = session?.modules ?? [];

  const services = useLive(() => runtime.mirror.services(), []);
  const products = useLive(() => runtime.mirror.products(), []);
  const certificates = useLive(() => runtime.mirror.certificates(), []);
  const clients = useLive(() => runtime.mirror.clients(), []);

  const appointment = prefill.appointment;
  const [clientId, setClientId] = useState(appointment?.client.client_id ?? '');
  const clientName =
    appointment?.client.display_name ??
    clients.find((c) => c.client_id === clientId)?.display_name ??
    undefined;

  const credit = useLive(
    async () =>
      clientId === ''
        ? { client_id: '', cash_cents: 0, gift_cents: 0 }
        : runtime.mirror.credit(clientId),
    { client_id: '', cash_cents: 0, gift_cents: 0 },
    [clientId],
  );

  const [lines, setLines] = useState<LineDraft[]>(() =>
    appointment === undefined ? [] : linesFromAppointment(appointment),
  );

  /* ------------------------------------------------------------------ */
  /* The pickup order, if this till was opened on one (C1 → B4)           */
  /* ------------------------------------------------------------------ */

  /**
   * **One read, no poll, and only when there is something to read.** The shelf
   * is a race with other people and has no IndexedDB row, so it is `useRemote`;
   * `pollMs: 0` because a draft the cashier has started editing must not be
   * yanked out from under their thumb.
   */
  const reservationId = prefill.reservationId;
  const draft = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: `/reservations/${reservationId as string}/lines`,
        schema: ReservationForTill,
        signal,
      }),
    [reservationId],
    { enabled: reservationId !== undefined, pollMs: 0 },
  );

  const [taxText, setTaxText] = useState('');
  const [taxOpen, setTaxOpen] = useState(false);
  const [discountText, setDiscountText] = useState('');
  const [discountOpen, setDiscountOpen] = useState(false);
  const [certCode, setCertCode] = useState('');
  const [certApplied, setCertApplied] = useState<string | null>(null);
  const [certAmountText, setCertAmountText] = useState('');
  const [useDeposit, setUseDeposit] = useState(appointment !== undefined);
  const [useCredit, setUseCredit] = useState(false);
  const [giftText, setGiftText] = useState('');
  const [cashText, setCashText] = useState('');
  const tillTenders = tillTendersFor(session);
  /**
   * **What she tapped** — and nothing until she taps. A till that opened on a
   * pre-chosen tender would record a guess for every sale rung without looking;
   * the Charge button says "choose how it was paid" instead.
   */
  const [tender, setTender] = useState<RemainderKind | null>(null);
  const [tenderLabel, setTenderLabel] = useState('');
  /** The first payment's amount — read only when there is a second one (0046). */
  const [firstTenderText, setFirstTenderText] = useState('');
  /** The extra payments (0046). Empty is every ordinary sale, byte for byte. */
  const [extraTenders, setExtraTenders] = useState<TenderDraft[]>([]);
  /** A tip, behind `session.till.tips` and off by default (0046). */
  const tipsOn = session?.till?.tips === true;
  const [tipText, setTipText] = useState('');
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * The sale, once it is queued. The panel says "rung up" rather than "paid":
   * the intent is in the outbox and may still be refused.
   */
  const [completed, setCompleted] = useState<CompletedSale | null>(null);

  /**
   * The draft, applied **once**, the moment it lands — a ref, because
   * re-applying over a cashier's edits (a `reload()`, a `StrictMode` remount)
   * is the honest failure mode.
   *
   * `unit_price_cents` is the price the guest was quoted and is carried as the
   * line's price text. Equal to the list, it sends no override and the line
   * records a zero discount; if the shelf moved, the quoted price is charged and
   * the gap is `discount = (list − charged) × qty` like every other gap.
   */
  const appliedDraft = useRef<string | null>(null);
  useEffect(() => {
    const data = draft.data;
    if (data === null || appliedDraft.current === data.reservation_id) return;
    appliedDraft.current = data.reservation_id;

    const usable = data.lines.filter(
      (line): line is typeof line & { item_id: string } => line.item_id !== null,
    );
    setLines(
      usable.map((line) => ({
        key: nextKey(),
        itemId: line.item_id,
        name: line.name,
        qty: line.qty,
        priceText: (line.unit_price_cents / 100).toFixed(2),
        // A reservation can never hold one: `open_price` items are excluded
        // from every guest projection in SQL (migration 0038).
        openPrice: false,
        note: '',
        fallbackListCents: line.unit_price_cents,
      })),
    );
    // The walk-in account is never a reservation's client (0039's WITH CHECK).
    if (data.client_id !== '') setClientId(data.client_id);
    if (usable.length !== data.lines.length) setError(t('checkout.reservationLineMissing'));
  }, [draft.data, t]);

  /**
   * **The tender this sale carries**: the tap, when the till still shows it.
   * The session comes out of IndexedDB a paint after mount, so a tap on a
   * button this shop does not draw is not a choice.
   */
  const chosenTender: RemainderKind | null =
    tender !== null && tillTenders.includes(tender) ? tender : null;
  /**
   * What the request names when nothing had to be chosen — a sale a gift card
   * or a deposit covered whole. The kind carries no money then, which is the
   * only case it is not asked for.
   */
  const effectiveTender: RemainderKind = chosenTender ?? tillTenders[0] ?? 'cash';

  const tenantId = session?.tenant.tenant_id ?? '';
  // Read once per mount: a picker that reshuffles under a thumb mis-rings.
  const recents = useMemo(() => readRecents(tenantId), [tenantId]);

  /** The catalog, as one lookup — a checkout line may name either kind (§7.3.1). */
  const listPriceOf = useMemo(() => {
    const prices = new Map<string, number>();
    for (const service of services) prices.set(service.service_id, service.price_cents);
    for (const product of products) prices.set(product.product_id, product.price_cents);
    return (itemId: string, fallback?: number): number => prices.get(itemId) ?? fallback ?? 0;
  }, [services, products]);

  const certificate =
    certApplied === null ? undefined : findCertificateByCode(certificates, certApplied);
  const certificateStateNow =
    certificate === undefined ? null : certificateState(certificate, today);

  const previewLines: PreviewLine[] = lines.map((line) => {
    /**
     * **The catalog price for *this* line** — for an optioned or attached line
     * the option's / the link's, never the item's headline (0048), or a ninety
     * at $255 against an $85 headline would record $170 given away.
     */
    const listUnitCents =
      line.optionId !== undefined || line.addOnOf !== undefined
        ? (line.fallbackListCents ?? listPriceOf(line.itemId))
        : listPriceOf(line.itemId, line.fallbackListCents);
    const typed = centsFromInput(line.priceText);
    return {
      itemId: line.itemId,
      itemName: line.name,
      qty: line.qty,
      listUnitCents,
      chargedUnitCents: typed ?? (line.openPrice ? 0 : listUnitCents),
      ...(line.openPrice ? { openPrice: true } : {}),
      ...(line.note.trim() === '' ? {} : { note: line.note.trim() }),
    };
  });

  /**
   * The deposit, honestly: `appointmentToWire` does not project
   * `prepaid_cents`, so an unknown deposit previews as **zero** (the total shown
   * is the most the guest could owe) and the request says `use_deposit` with no
   * amount — "whatever is left of it".
   */
  const depositAvailable = appointment?.prepaid_cents ?? 0;
  const depositKnown = appointment?.prepaid_cents !== undefined;

  /**
   * **The split, as the server will read it** (0046): every entry but the last
   * carries an amount, the last takes the rest. `undefined` while there are no
   * extra rows, which leaves the ordinary sale's request exactly as it was.
   */
  const splitDrafts: TenderDraft[] =
    extraTenders.length === 0
      ? []
      : [
          { key: 'first', kind: effectiveTender, text: firstTenderText, label: tenderLabel },
          ...extraTenders,
        ];
  const splitParts =
    splitDrafts.length === 0
      ? undefined
      : splitDrafts.map((part) => ({
          kind: part.kind,
          // A blank box is a blank amount, wherever it is — the rule is the
          // server's: a blank last row takes the rest, a blank earlier one is
          // refused, a typed last row has to make the sum exact.
          ...(centsFromInput(part.text) === null
            ? {}
            : { cents: centsFromInput(part.text) as number }),
          ...(part.kind === 'other' && part.label.trim() !== ''
            ? { label: part.label.trim() }
            : {}),
        }));

  /** Only when the owner asked for the field. An off switch is not a zero. */
  const tipCents = tipsOn ? (centsFromInput(tipText) ?? 0) : 0;

  const input: PreviewInput = {
    lines: previewLines,
    taxCents: centsFromInput(taxText) ?? 0,
    remainderKind: effectiveTender,
    ...(splitParts === undefined ? {} : { remainders: splitParts }),
    ...(tipCents === 0 ? {} : { tipCents }),
    ...(effectiveTender === 'other' && tenderLabel.trim() !== ''
      ? { remainderLabel: tenderLabel.trim() }
      : {}),
    ...(centsFromInput(discountText) === null
      ? {}
      : { manualDiscount: { cents: centsFromInput(discountText) as number } }),
    ...(certificate === undefined || certificateStateNow !== 'open'
      ? {}
      : {
          certificate: {
            code: certificate.code,
            availableCents: certificate.remaining_cents,
            ...(centsFromInput(certAmountText) === null
              ? {}
              : { cents: centsFromInput(certAmountText) as number }),
          },
        }),
    ...(useDeposit && appointment !== undefined
      ? { deposit: { availableCents: depositAvailable } }
      : {}),
    ...(useCredit
      ? {
          credit: {
            gift: {
              availableCents: credit.gift_cents,
              ...(centsFromInput(giftText) === null
                ? {}
                : { cents: centsFromInput(giftText) as number }),
            },
            cash: {
              availableCents: credit.cash_cents,
              ...(centsFromInput(cashText) === null
                ? {}
                : { cents: centsFromInput(cashText) as number }),
            },
          },
        }
      : {}),
  };

  const preview: WaterfallPreview = previewWaterfall(input);

  /* ---- adding things ---- */

  const addService = (service: Service, option?: ServiceOption): void => {
    setError(null);
    setLines((current) => [
      ...current,
      {
        key: nextKey(),
        itemId: service.service_id,
        name: service.name,
        qty: 1,
        priceText: '',
        openPrice: false,
        note: '',
        ...(option === undefined
          ? {}
          : {
              // The option's price is this line's list, and its id tells the
              // server which catalog number to price it from (0048).
              optionId: option.option_id,
              fallbackListCents: option.price_cents,
              lengthLabel:
                option.duration_min === null
                  ? option.name
                  : t('checkout.minutes', { minutes: option.duration_min }),
            }),
      },
    ]);
  };

  const addAddOn = (parent: Service, addOn: Service['add_ons'][number]): void => {
    setError(null);
    setLines((current) => [
      ...current,
      {
        key: nextKey(),
        itemId: addOn.service_id,
        name: addOn.name,
        qty: 1,
        priceText: '',
        openPrice: false,
        note: '',
        // What it costs *attached*, and the parent that makes it so (0048).
        fallbackListCents: addOn.price_cents,
        addOnOf: parent.service_id,
      },
    ]);
  };

  const pickProduct = (product: Product): void => {
    setError(null);
    // An open-price line is incomplete until it has a price: ask first, add after.
    if (product.open_price) return setPricing({ kind: 'new', product });
    setLines((current) => [
      ...current,
      {
        key: nextKey(),
        itemId: product.product_id,
        name: product.name,
        qty: 1,
        priceText: '',
        openPrice: false,
        note: '',
      },
    ]);
  };

  const changeLine = (key: string, apply: (line: LineDraft) => LineDraft): void => {
    setLines((current) => current.map((line) => (line.key === key ? apply(line) : line)));
  };

  /**
   * Take a line off — and, for a service, whatever was attached to it: an
   * add-on with no parent on the sale would be priced by a link that is no
   * longer there.
   */
  const removeLine = (key: string): void => {
    setLines((current) => {
      const gone = current.find((line) => line.key === key);
      const orphaned =
        gone === undefined || gone.addOnOf !== undefined
          ? []
          : current.some(
                (l) => l.key !== key && l.itemId === gone.itemId && l.addOnOf === undefined,
              )
            ? []
            : current.filter((l) => l.addOnOf === gone.itemId).map((l) => l.key);
      return current.filter((line) => line.key !== key && !orphaned.includes(line.key));
    });
  };

  /* ---- the split (0046) ---- */

  /**
   * Add a payment. The new row starts on the till's first button — a split is
   * almost always two *different* ways of paying.
   */
  const addPart = (): void => {
    setExtraTenders((current) => [
      ...current,
      { key: nextTenderKey(), kind: tillTenders[0] ?? 'cash', text: '', label: '' },
    ]);
  };

  const changePart = (key: string, apply: (part: TenderDraft) => TenderDraft): void => {
    setExtraTenders((current) => current.map((part) => (part.key === key ? apply(part) : part)));
  };

  /** Remove a payment, and forget the first row's amount when the last extra goes. */
  const removePart = (key: string): void => {
    setExtraTenders((current) => {
      const next = current.filter((part) => part.key !== key);
      if (next.length === 0) setFirstTenderText('');
      return next;
    });
  };

  /* ---- the one button ---- */

  const owedNow = preview.ok ? preview.applied.remainderCents : null;
  const tenderNeeded = owedNow === null || owedNow > 0;
  /** Why Charge is off, in plain words — or `null` when it is on. */
  const blocked: string | null =
    lines.length === 0
      ? t('checkout.whyEmpty')
      : !preview.ok
        ? // The server's own sentence (`money/waterfall.ts`), printed as it came.
          preview.refusal.message
        : tenderNeeded && chosenTender === null
          ? t('checkout.whyTender')
          : null;

  const chargeLabel =
    owedNow === null
      ? t('checkout.chargePlain')
      : owedNow === 0
        ? t('checkout.completeSale')
        : chosenTender === null
          ? t('checkout.chargeAmount', { amount: formatMoney(owedNow, currency, locale) })
          : t('checkout.charge', {
              amount: formatMoney(owedNow, currency, locale),
              tender:
                extraTenders.length > 0
                  ? t('checkout.chargeSplit')
                  : t(`checkout.tenderButton.${chosenTender}`),
            });

  const submit = async (): Promise<void> => {
    if (lines.length === 0) return setError(t('checkout.needsALine'));
    // The waterfall's refusals are the server's own, words included.
    if (!preview.ok) return setError(preview.refusal.message);
    if (blocked !== null) return setError(blocked);

    await actions.checkout({
      lines: lines.map((line, index) => {
        const previewLine = previewLines[index];
        // An open-price line **always** sends its amount: there is no catalog
        // price for the server to fall back on.
        const overridden =
          previewLine !== undefined &&
          (line.openPrice || previewLine.chargedUnitCents !== previewLine.listUnitCents);
        return {
          itemId: line.itemId,
          qty: line.qty,
          ...(overridden ? { unitPriceCents: previewLine.chargedUnitCents } : {}),
          ...(line.note.trim() === '' ? {} : { note: line.note.trim() }),
          // Ids, so the server prices the same list this screen showed (0048)
          // — they say *which* catalog number, the opposite of naming one.
          ...(line.optionId === undefined ? {} : { optionId: line.optionId }),
          ...(line.addOnOf === undefined ? {} : { addOnOf: line.addOnOf }),
        };
      }),
      tender: effectiveTender,
      ...(effectiveTender === 'other' && tenderLabel.trim() !== ''
        ? { tenderLabel: tenderLabel.trim() }
        : {}),
      // The split, and only when there is one — from the same list the preview
      // was computed from (0046).
      ...(splitParts === undefined ? {} : { tenders: splitParts.map(toActionPart) }),
      ...(tipCents === 0 ? {} : { tipCents }),
      ...(appointment === undefined ? {} : { appointmentId: appointment.appointment_id }),
      ...(appointment !== undefined || clientId === '' ? {} : { clientId }),
      ...(clientName === undefined ? {} : { clientName }),
      ...(centsFromInput(taxText) === null ? {} : { taxCents: centsFromInput(taxText) as number }),
      ...(centsFromInput(discountText) === null
        ? {}
        : { discountCents: centsFromInput(discountText) as number }),
      ...(certificate === undefined || certificateStateNow !== 'open'
        ? {}
        : {
            certificateCode: certificate.code,
            ...(centsFromInput(certAmountText) === null
              ? {}
              : { certificateCents: centsFromInput(certAmountText) as number }),
          }),
      ...(appointment === undefined ? {} : { useDeposit }),
      ...(useCredit
        ? {
            useCredit: true,
            ...(centsFromInput(giftText) === null
              ? {}
              : { creditGiftCents: centsFromInput(giftText) as number }),
            ...(centsFromInput(cashText) === null
              ? {}
              : { creditCashCents: centsFromInput(cashText) as number }),
          }
        : {}),
      ...(note.trim() === '' ? {} : { note: note.trim() }),
    });
    /*
     * **The shelf, second** (C1 → B4) — after the settlement, in the same tap,
     * because the outbox is a total order (spec §8.2). Only when the shelf does
     * not already say so: `collected → collected` is not a legal move.
     */
    const reservation = draft.data;
    if (reservation !== null && reservation.status !== 'collected') {
      await actions.collectReservation({
        reservationId: reservation.reservation_id,
        ...(reservation.client_name === null ? {} : { clientName: reservation.client_name }),
      });
    }

    // Only after the intent is queued: an abandoned sale does not reorder her shelf.
    rememberRecents(
      tenantId,
      lines.map((line) => line.itemId),
    );
    setCompleted({
      totalCents: preview.totalCents,
      collectedCents: preview.totalCents + preview.tipCents,
      tenders: preview.applied.remainders.map((part) => ({
        kind: part.kind as RemainderKind,
        cents: part.amount_cents,
      })),
      // The walk-in account is a real row but nobody in particular.
      clientId: appointment?.client.client_id ?? (clientId === '' ? null : clientId),
      clientName: clientName ?? null,
    });
  };

  /*
   * The beat after the sale: what it came to, how it was paid, the slot B1's
   * note button goes in, and one way out. **Rung up**, not "paid" — the intent
   * is in the outbox and the server has not answered yet (spec §8.1).
   */
  if (completed !== null) {
    return (
      <div className="sheet" role="dialog" aria-label={t('checkout.doneDialogLabel')}>
        <div className="sheet__panel">
          <h2>{t('checkout.doneTitle')}</h2>
          <p className="pos-done__sum">
            <strong>{formatMoney(completed.collectedCents, currency, locale)}</strong>
            <span>
              {' '}
              {t('checkout.doneTender', {
                tender: completed.tenders
                  .map((part) => t(`checkout.tender.${part.kind}`))
                  .join(' + '),
              })}
            </span>
          </p>
          <p className="muted">{t('checkout.doneQueued')}</p>

          {/* MOUNT POINT — the desk agent's `<AddClientNote>` (B1). */}
          {afterCheckout === undefined
            ? null
            : afterCheckout({ clientId: completed.clientId, clientName: completed.clientName })}

          <div className="sheet__actions">
            <button type="button" className="button button--primary" onClick={onDone}>
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pricingLine =
    pricing?.kind === 'edit' ? lines.find((line) => line.key === pricing.key) : undefined;
  const listSubtotalCents = previewLines.reduce((sum, l) => sum + l.listUnitCents * l.qty, 0);

  return (
    <div className="sheet sheet--pos" role="dialog" aria-label={t('checkout.dialogLabel')}>
      <div className="sheet__panel pos">
        <header className="pos__header">
          <h2>
            {appointment === undefined
              ? t('checkout.quickSale')
              : t('checkout.closeOut', { name: clientName ?? t('checkout.guestFallback') })}
          </h2>
          <button type="button" className="button button--ghost" onClick={onCancel}>
            {t('common.cancel')}
          </button>
        </header>

        <section className="pos__catalog" aria-label={t('checkout.catalogLabel')}>
          <Catalog
            services={services}
            products={products}
            lines={lines}
            recents={recents}
            currency={currency}
            locale={locale}
            t={t}
            onAddService={addService}
            onAddAddOn={addAddOn}
            onPickProduct={pickProduct}
          />
        </section>

        <section className="pos__cart" aria-label={t('checkout.cartSection')}>
          <GuestField
            clients={clients}
            clientId={clientId}
            fixedName={appointment === undefined ? undefined : clientName}
            onChange={setClientId}
            t={t}
          />

          {/*
              C1 → B4: say out loud that these lines are a promise being
              collected, at the prices the guest was quoted.
            */}
          {draft.data === null ? null : (
            <p className="pos-cart__from muted">
              {t('checkout.fromReservation', {
                name: draft.data.client_name ?? t('checkout.guestFallback'),
              })}
            </p>
          )}

          <CartLines
            lines={lines}
            previewLines={previewLines}
            currency={currency}
            locale={locale}
            t={t}
            onQty={(key, qty) => changeLine(key, (l) => ({ ...l, qty: Math.max(1, qty) }))}
            onRemove={removeLine}
            onEditPrice={(key) => setPricing({ kind: 'edit', key })}
          />

          {lines.length === 0 ? null : (
            <>
              <Totals
                preview={preview}
                listSubtotalCents={listSubtotalCents}
                currency={currency}
                locale={locale}
                t={t}
                discount={{
                  open: discountOpen,
                  text: discountText,
                  label: t('checkout.discountLabel'),
                  addLabel: t('checkout.addDiscount'),
                  removeLabel: t('checkout.removeDiscount'),
                  onOpen: () => setDiscountOpen(true),
                  onChange: setDiscountText,
                  onRemove: () => {
                    setDiscountText('');
                    setDiscountOpen(false);
                  },
                }}
                tax={{
                  open: taxOpen,
                  text: taxText,
                  label: t('checkout.tax'),
                  addLabel: t('checkout.addTax'),
                  removeLabel: t('checkout.removeTax'),
                  onOpen: () => setTaxOpen(true),
                  onChange: setTaxText,
                  onRemove: () => {
                    setTaxText('');
                    setTaxOpen(false);
                  },
                }}
              />

              <Payment
                t={t}
                currency={currency}
                locale={locale}
                tillTenders={tillTenders}
                chosen={chosenTender}
                onChoose={(kind) => {
                  setTender(kind);
                  setError(null);
                }}
                tenderLabel={tenderLabel}
                onTenderLabel={setTenderLabel}
                extraTenders={extraTenders}
                firstTenderText={firstTenderText}
                onFirstTenderText={setFirstTenderText}
                onAddPart={addPart}
                onChangePart={changePart}
                onRemovePart={removePart}
                certificatesOn={modules.includes('certificates')}
                certCode={certCode}
                onCertCode={setCertCode}
                certApplied={certApplied}
                onLookUp={() => setCertApplied(certCode.trim() === '' ? null : certCode.trim())}
                onForgetCertificate={() => {
                  setCertApplied(null);
                  setCertCode('');
                  setCertAmountText('');
                }}
                certificate={certificate}
                certificateState={certificateStateNow}
                certAmountText={certAmountText}
                onCertAmountText={setCertAmountText}
                depositOffered={appointment !== undefined}
                useDeposit={useDeposit}
                onUseDeposit={setUseDeposit}
                depositKnown={depositKnown}
                depositAvailableCents={depositAvailable}
                creditOffered={modules.includes('credit') && clientId !== ''}
                useCredit={useCredit}
                onUseCredit={setUseCredit}
                creditGiftCents={credit.gift_cents}
                creditCashCents={credit.cash_cents}
                giftText={giftText}
                onGiftText={setGiftText}
                cashText={cashText}
                onCashText={setCashText}
                tipsOn={tipsOn}
                tipText={tipText}
                onTipText={setTipText}
                tipBaseCents={preview.ok ? preview.totalCents - preview.taxCents : 0}
              />

              {noteOpen ? (
                <label className="field pos-note">
                  <span>{t('checkout.note')}</span>
                  <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
                </label>
              ) : (
                <button
                  type="button"
                  className="link-button pos-note__add"
                  onClick={() => setNoteOpen(true)}
                >
                  {t('checkout.addNote')}
                </button>
              )}
            </>
          )}

          {error !== null && error !== blocked ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : null}
        </section>

        {/*
              The one button — a direct child of the panel so it can stay stuck
              to the bottom of a phone screen while the catalog scrolls, and sit
              under the cart on an iPad. It names what it will do ("Charge
              $78.00 · Cash") and, when it cannot, why not.
            */}
        <div className="pos-charge">
          <div className="pos-charge__total">
            <span>{t('checkout.totalLabel')}</span>
            <strong>
              {preview.ok
                ? formatMoney(preview.totalCents, currency, locale)
                : formatMoney(listSubtotalCents, currency, locale)}
            </strong>
          </div>
          <button
            type="button"
            className="button button--primary pos-charge__button"
            disabled={blocked !== null}
            aria-describedby={blocked === null ? undefined : 'pos-charge-why'}
            onClick={() => void submit()}
          >
            {chargeLabel}
          </button>
          {blocked === null ? null : (
            <p id="pos-charge-why" className="pos-charge__why" aria-live="polite">
              {blocked}
            </p>
          )}
        </div>

        {pricing === null ? null : (
          <PriceSheet
            key={pricing.kind === 'edit' ? pricing.key : pricing.product.product_id}
            name={pricing.kind === 'new' ? pricing.product.name : (pricingLine?.name ?? '')}
            initialPrice={pricingLine?.priceText ?? ''}
            initialNote={pricingLine?.note ?? ''}
            confirmLabel={
              pricing.kind === 'new' ? t('checkout.priceSheetAdd') : t('checkout.priceSheetSave')
            }
            currency={currency}
            locale={locale}
            t={t}
            onCancel={() => setPricing(null)}
            onDone={(priceText, lineNote) => {
              if (pricing.kind === 'edit') {
                changeLine(pricing.key, (l) => ({ ...l, priceText, note: lineNote }));
              } else {
                const product = pricing.product;
                setLines((current) => [
                  ...current,
                  {
                    key: nextKey(),
                    itemId: product.product_id,
                    name: product.name,
                    qty: 1,
                    priceText,
                    openPrice: true,
                    note: lineNote,
                  },
                ]);
              }
              setPricing(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
